import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  GOODS_STUDIO_COUNTRY_OPTIONS,
  GOODS_STUDIO_DEFAULT_PARAMS,
  GOODS_STUDIO_LANGUAGE_OPTIONS,
  GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS,
  GOODS_STUDIO_PRODUCT_INFO_MAX,
  getDefaultGoodsStudioCountryStyle,
  getGoodsStudioCountryCopy,
  getGoodsStudioCountryStyleLabel,
  getGoodsStudioCountryStyles,
  normalizeGoodsStudioCountryStyle,
} from '@/pages/ipe/workbench/constants/aiStudioConfig';
import useStudioSidebarDockLeft from '@/pages/ipe/workbench/hooks/useStudioSidebarDockLeft';
import { useHistoryPanelTransition } from '@/pages/ipe/workbench/hooks/useHistoryPanelTransition';
import { resolveLockedTitlePresetScope } from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import '@/styles/ipe/workbench/modals/Text2ImgPromptCaseFormModal.css';
import '@/styles/ipe/workbench/modals/GoodsStudioPromptCaseFormModal.css';

function emptyForm(scope = 'personal', defaults = {}) {
  const country = defaults.country || GOODS_STUDIO_DEFAULT_PARAMS.country;
  return {
    name: String(defaults.name || ''),
    productInfo: String(defaults.productInfo || ''),
    country,
    countryStyle: normalizeGoodsStudioCountryStyle(country, defaults.countryStyle),
    language: defaults.language || GOODS_STUDIO_DEFAULT_PARAMS.language,
    mainImageRatio: defaults.mainImageRatio || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio,
    caseScope: scope === 'team' ? 'team' : 'personal',
  };
}

function formFromCase(item, fallbackScope = 'personal') {
  if (!item) return emptyForm(fallbackScope);
  const country = item.country || GOODS_STUDIO_DEFAULT_PARAMS.country;
  return {
    name: String(item.name || ''),
    productInfo: String(item.productInfo || ''),
    country,
    countryStyle: normalizeGoodsStudioCountryStyle(country, item.countryStyle),
    language: item.language || GOODS_STUDIO_DEFAULT_PARAMS.language,
    mainImageRatio: item.mainImageRatio || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio,
    caseScope: item.scope === 'team' ? 'team' : 'personal',
  };
}

function ratioPreviewStyle(ratio) {
  const parts = String(ratio || '1:1').split(':').map((n) => Number(n) || 1);
  const w = parts[0] || 1;
  const h = parts[1] || 1;
  const max = 22;
  const scale = max / Math.max(w, h);
  return {
    width: Math.max(8, Math.round(w * scale)),
    height: Math.max(8, Math.round(h * scale)),
  };
}

export function validateGoodsStudioPromptCaseForm(form, t) {
  const tr = typeof t === 'function' ? t : (key, opts) => (opts?.defaultValue ?? key);
  const name = String(form?.name || '').trim();
  const productInfo = String(form?.productInfo || '').trim();
  if (!name) return tr('promptCase.errNameRequired');
  if (productInfo.length > GOODS_STUDIO_PRODUCT_INFO_MAX) {
    return tr('promptCase.errProductInfoTooLong', { max: GOODS_STUDIO_PRODUCT_INFO_MAX });
  }
  if (!GOODS_STUDIO_COUNTRY_OPTIONS.some((item) => item.value === form?.country)) {
    return tr('promptCase.errCountryRequired');
  }
  const styles = getGoodsStudioCountryStyles(form.country);
  if (!styles.some((item) => item.value === form?.countryStyle)) {
    return tr('promptCase.errMarketStyleRequired');
  }
  if (!GOODS_STUDIO_LANGUAGE_OPTIONS.some((item) => item.value === form?.language)) {
    return tr('promptCase.errLanguageRequired');
  }
  if (!GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS.some((item) => item.value === form?.mainImageRatio)) {
    return tr('promptCase.errImageRatioRequired');
  }
  return null;
}

export function buildGoodsStudioPromptCasePayload(form) {
  const country = form?.country || GOODS_STUDIO_DEFAULT_PARAMS.country;
  return {
    name: String(form?.name || '').trim(),
    productInfo: String(form?.productInfo || '').trim(),
    country,
    countryStyle: normalizeGoodsStudioCountryStyle(country, form?.countryStyle),
    language: form?.language || GOODS_STUDIO_DEFAULT_PARAMS.language,
    mainImageRatio: form?.mainImageRatio || GOODS_STUDIO_DEFAULT_PARAMS.mainImageRatio,
    caseScope: form?.caseScope === 'team' ? 'team' : 'personal',
  };
}

/**
 * 主图创作模板 · 新建 / 编辑表单
 */
export default function GoodsStudioPromptCaseFormModal({
  isOpen,
  mode = 'create',
  /** 可选：工作台顶栏模式（不传则自动跟随顶栏）。弹窗内不可切换归属 */
  workspaceMode,
  caseItem = null,
  defaults = null,
  canAccessTeamPresets = false,
  saving = false,
  onClose,
  onSubmit,
  /** 居中弹窗：工作流节点参数面板内使用（与模板库保持一致） */
  centered = false,
}) {
  const isEdit = mode === 'edit';
  const { t } = useTranslation('workbench');
  const {
    mounted,
    exiting,
    requestClose,
    handlePanelAnimationEnd,
  } = useHistoryPanelTransition(isOpen, onClose, {
    baseClassName: 't2i-case-form-modal-layer',
    deferContent: false,
    closeNotifiesParent: true,
  });
  const dockLeft = useStudioSidebarDockLeft(mounted && !centered);
  const workspaceModeFromHook = useIpeWorkspaceMode();
  const lockedScope = resolveLockedTitlePresetScope(
    workspaceMode || workspaceModeFromHook,
    canAccessTeamPresets,
  );
  const [form, setForm] = useState(() => emptyForm(lockedScope, defaults || {}));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(formFromCase(caseItem, lockedScope));
    } else {
      setForm(emptyForm(lockedScope, defaults || {}));
    }
    setError('');
  }, [isOpen, isEdit, caseItem, lockedScope, defaults]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, saving, requestClose]);

  const productInfoLen = String(form.productInfo || '').length;
  const countryLabel = GOODS_STUDIO_COUNTRY_OPTIONS.find((item) => item.value === form.country)?.label || '';
  const languageLabel = GOODS_STUDIO_LANGUAGE_OPTIONS.find((item) => item.value === form.language)?.label || '';
  const countryStyles = useMemo(() => getGoodsStudioCountryStyles(form.country), [form.country]);
  const countryStyleLabel = useMemo(
    () => getGoodsStudioCountryStyleLabel(form.country, form.countryStyle),
    [form.country, form.countryStyle],
  );
  const countryCopy = useMemo(
    () => getGoodsStudioCountryCopy(form.country, form.countryStyle),
    [form.country, form.countryStyle],
  );
  const canSubmit = useMemo(() => Boolean(String(form.name || '').trim()) && !saving, [form.name, saving]);

  if (!mounted) return null;

  const setField = (key, value) => {
    setForm((prev) => {
      if (key === 'country') {
        return {
          ...prev,
          country: value,
          countryStyle: getDefaultGoodsStudioCountryStyle(value),
        };
      }
      return { ...prev, [key]: value };
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const msg = validateGoodsStudioPromptCaseForm(form, t);
    if (msg) {
      setError(msg);
      return;
    }
    const payload = buildGoodsStudioPromptCasePayload(form);
    await onSubmit?.(payload, { mode, caseItem });
  };

  const safeClose = () => {
    if (!saving) requestClose();
  };

  return createPortal(
    <div
      className={`funding-modal funding-modal--top t2i-case-form-modal-layer gs-case-form-modal-layer${centered ? ' ipe-case-modal--centered' : ''}${exiting ? ' is-closing' : ' is-opening'}`}
      style={{ '--t2i-case-library-dock-left': `${dockLeft}px` }}
    >
      <div
        className="funding-modal__backdrop"
        role="presentation"
        onClick={safeClose}
      />
      <div
        className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--t2i-case-form funding-modal__panel--gs-case-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gs-case-form-title"
        onAnimationEnd={handlePanelAnimationEnd}
      >
        <header className="funding-modal__header t2i-case-form__header">
          <div className="t2i-case-form__header-text">
            <h3 id="gs-case-form-title">{isEdit ? t('promptCase.editTemplate') : t('promptCase.newTemplate')}</h3>
            <p className="t2i-case-form__subtitle">
              {t('promptCase.goodsSubtitle')}
            </p>
          </div>
        </header>

        <form className="funding-modal__body t2i-case-form__body gs-case-form__body" onSubmit={handleSubmit}>
          <section className="t2i-case-form__section">
            <label className="t2i-case-form__label" htmlFor="gs-case-name">
              {t('promptCase.name')}<span className="t2i-case-form__req">*</span>
            </label>
            <input
              id="gs-case-name"
              type="text"
              className="t2i-case-form__input"
              value={form.name}
              maxLength={128}
              disabled={saving}
              onChange={(e) => setField('name', e.target.value)}
              placeholder={t('promptCase.goodsNamePh')}
            />
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <label className="t2i-case-form__label" htmlFor="gs-case-product-info">
                {t('promptCase.productInfo')}
              </label>
              <span className={`t2i-case-form__count${productInfoLen >= GOODS_STUDIO_PRODUCT_INFO_MAX ? ' is-limit' : ''}`}>
                {productInfoLen}/{GOODS_STUDIO_PRODUCT_INFO_MAX}
              </span>
            </div>
            <textarea
              id="gs-case-product-info"
              className="t2i-case-form__textarea"
              value={form.productInfo}
              maxLength={GOODS_STUDIO_PRODUCT_INFO_MAX}
              disabled={saving}
              rows={4}
              onChange={(e) => setField('productInfo', e.target.value)}
              placeholder={t('promptCase.productInfoPh')}
            />
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.country')}</span>
              <span className="t2i-case-form__meta">{countryLabel}</span>
            </div>
            <div className="t2i-case-form__chip-row gs-case-form__country-chips" role="group" aria-label={t('promptCase.country')}>
              {GOODS_STUDIO_COUNTRY_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`t2i-case-form__chip${form.country === item.value ? ' is-active' : ''}`}
                  onClick={() => setField('country', item.value)}
                  disabled={saving}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.marketStyle')}</span>
              <span className="t2i-case-form__meta">{countryStyleLabel}</span>
            </div>
            <div className="t2i-case-form__chip-row gs-case-form__style-chips" role="group" aria-label={t('promptCase.marketStyle')}>
              {countryStyles.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`t2i-case-form__chip${form.countryStyle === item.value ? ' is-active' : ''}`}
                  onClick={() => setField('countryStyle', item.value)}
                  disabled={saving}
                >
                  {item.label}
                </button>
              ))}
            </div>
            {countryCopy ? (
              <p className="gs-case-form__country-copy" aria-live="polite">
                {countryCopy}
              </p>
            ) : null}
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.language')}</span>
              <span className="t2i-case-form__meta">{languageLabel}</span>
            </div>
            <div className="t2i-case-form__chip-row" role="group" aria-label={t('promptCase.language')}>
              {GOODS_STUDIO_LANGUAGE_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`t2i-case-form__chip${form.language === item.value ? ' is-active' : ''}`}
                  onClick={() => setField('language', item.value)}
                  disabled={saving}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </section>

          <section className="t2i-case-form__section">
            <div className="t2i-case-form__label-row">
              <span className="t2i-case-form__label">{t('promptCase.imageRatio')}</span>
              <span className="t2i-case-form__meta">{form.mainImageRatio}</span>
            </div>
            <div className="t2i-case-form__ratio-row gs-case-form__ratio-row" role="group" aria-label={t('promptCase.imageRatio')}>
              {GOODS_STUDIO_MAIN_IMAGE_RATIO_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`t2i-case-form__ratio${form.mainImageRatio === item.value ? ' is-active' : ''}`}
                  onClick={() => setField('mainImageRatio', item.value)}
                  disabled={saving}
                >
                  <span className="t2i-case-form__ratio-frame" aria-hidden="true">
                    <span
                      className="t2i-case-form__ratio-box"
                      style={ratioPreviewStyle(item.value)}
                    />
                  </span>
                  <span className="t2i-case-form__ratio-text">{item.label}</span>
                </button>
              ))}
            </div>
          </section>

          {error ? (
            <p className="t2i-case-form__error" role="alert">{error}</p>
          ) : null}

          <footer className="t2i-case-form__footer">
            <button
              type="button"
              className="funding-button funding-button--ghost t2i-case-form__btn"
              onClick={onClose}
              disabled={saving}
            >
              {t('promptCase.cancel')}
            </button>
            <button
              type="submit"
              className="funding-button funding-button--accent t2i-case-form__btn t2i-case-form__btn--primary"
              disabled={!canSubmit}
            >
              {saving
                ? t('promptCase.saving')
                : (isEdit ? t('promptCase.saveChanges') : t('promptCase.createTemplate'))}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body,
  );
}
